import type { Page, PartFittingView, TruckPartsListQuery } from '@chukua/common';
import { ShieldAlert, Wrench } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import type { ListQuerySchema } from '../../../../../shared/policies/list-params';
import { DirectoryTable } from '../../../components/DirectoryTable';
import { useDirectoryList } from '../../../hooks/use-directory-list';
import { usePartTypeOptions } from '../hooks/use-part-type-options';
import { buildPartColumns } from './build-part-columns';
import { PartsToolbar } from './PartsToolbar';

/** The query both lists share: the register's adds only a truck filter, so its schema and fetch fit here. */
interface PartsRegisterTableProps {
  schema: ListQuerySchema<TruckPartsListQuery>;
  /** Must be stable between renders: the list re-fetches when it changes. */
  fetchPage: (query: TruckPartsListQuery) => Promise<Page<PartFittingView>>;
  showTruck: boolean;
  /** Hide the part type picker where the page already is one type. */
  showPartTypeFilter?: boolean;
  emptyDescription: string;
  searchPlaceholder: string;
}

/** The parts fitted register as a server-paged table: search, part type and date filters, sorting and paging, all in the URL query. */
export function PartsRegisterTable({ schema, fetchPage, showTruck, showPartTypeFilter = true, emptyDescription, searchPlaceholder }: PartsRegisterTableProps) {
  const { state, list, isRefreshing } = useDirectoryList(schema, fetchPage);
  const partTypes = usePartTypeOptions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <TableSkeleton label="Loading parts" withSearch />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load parts" description="Something went wrong loading the register. Try reloading the page." />;
  }

  return (
    <DirectoryTable
      page={state.page}
      columns={buildPartColumns({ list: list, showTruck })}
      list={list}
      isRefreshing={isRefreshing}
      newButton={null}
      emptyIcon={Wrench}
      emptyTitle="No parts fitted yet"
      emptyDescription={emptyDescription}
      searchPlaceholder={searchPlaceholder}
      noMatchTitle="No matching parts"
      toolbar={<PartsToolbar list={list} partTypes={showPartTypeFilter ? partTypes : null} />}
    />
  );
}
